import { getLikeTemplateListApi, likeTemplateApi, queryMyFiles, queryRecentModifyFiles, removeLikeTemplateApi } from "@/apis"
import { DiagramData, Folder } from "@shared/types"
import { TemplateData } from "@shared/types"
import dayjs from "dayjs"
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import { makeAutoObservable, toJS } from "mobx"
import { createContext } from "react"
import { DiagramStore } from "./diagram"
import { LibraryStore } from "./library"
import { UserStore } from "./user"
import { TemplateStore } from "./template"
import { NotificationStore } from "./notification"

dayjs.extend(isToday)
dayjs.extend(isYesterday)

const __FOLDER_BREADCRUMB_KEY__ = '__CHENG_ZI_FOLDER_BREADCRUMB_KEY__'
const __CURRENT_FOLDER_KEY__ = '__CHENG_ZI_CURRENT_FOLDER_KEY__'

export class EasyDrawStore {
  folderBreadcrumb: Folder[] = []
  currentFolder: Folder | null = null
  folderList: Folder[] = []
  fileList: DiagramData[] = []
  favoriteTemplates: TemplateData[] = []
  favoriteTemplateIds: string[] = []
  recentModifiedFiles: DiagramData[] = []

  diagramStore: DiagramStore
  userStore: UserStore
  libraryStore: LibraryStore
  templateStore: TemplateStore
  notificationStore: NotificationStore

  loading: boolean = false
  isLogin: boolean = false

  constructor() {
    makeAutoObservable(this)
    this.diagramStore = new DiagramStore(this)
    this.userStore = new UserStore(this)
    this.libraryStore = new LibraryStore(this)
    this.templateStore = new TemplateStore(this)
    this.notificationStore = new NotificationStore(this)
    this.loadStoreFromLS()
  }
  loadStoreFromLS() {
    try {
      const folderBreadcrumb = JSON.parse(window.localStorage.getItem(__FOLDER_BREADCRUMB_KEY__) || '[]')
      const currentFolder = JSON.parse(window.localStorage.getItem(__CURRENT_FOLDER_KEY__) || null)
      this.folderBreadcrumb = folderBreadcrumb
      this.currentFolder = currentFolder
    } catch (e) {
    }
  }

  saveStoreToLS() {
    try {
      window.localStorage.setItem(__FOLDER_BREADCRUMB_KEY__, JSON.stringify(toJS(this.folderBreadcrumb)))
      window.localStorage.setItem(__CURRENT_FOLDER_KEY__, JSON.stringify(toJS(this.currentFolder)))
    } catch (error) {
    }
  }

  pushFolderBreadCrumbItem(folder: Folder) {
    this.folderBreadcrumb.push(folder)
    this.currentFolder = folder
    this.saveStoreToLS()
  }

  popFolderBreadCrumb(folder?: Folder) {
    if (!folder) {
      const folders = [...this.folderBreadcrumb]
      this.folderBreadcrumb = []
      this.currentFolder = null
      this.saveStoreToLS()
      return
    }
    const folders = []
    let idx = 0
    for (let i = 0; i < this.folderBreadcrumb.length; i++) {
      idx++
      folders.push(this.folderBreadcrumb[i])
      if (this.folderBreadcrumb[i].id === folder.id) {
        break
      }
    }
    this.currentFolder = folders[idx - 1]
    this.folderBreadcrumb = folders
    this.saveStoreToLS()
  }

  async fetchMyFiles(filter?: {
    isDeleted: boolean
    isAll: boolean
  }) {
    const { isDeleted = false, isAll = false } = filter || {}
    this.loading = true
    const res = await queryMyFiles(isAll ? 'ALL' : this.currentFolder?.id, isDeleted)
    const { files, folders } = res.data || {}
    this.fileList = files?.list || []
    this.folderList = folders?.list || []
    this.loading = false
  }

  async fetchRecentFiles() {
    this.loading = true
    const res = await queryRecentModifyFiles()
    this.recentModifiedFiles = res.data?.list || []
    this.loading = false
  }

  get todayModifiedFiles() {
    return this.recentModifiedFiles.filter(file => {
      return dayjs(file.updateTime).isToday()
    })
  }

  get yesterdayModifiedFiles() {
    return this.recentModifiedFiles.filter(file => {
      return dayjs(file.updateTime).isYesterday()
    })
  }

  get thisWeekModifiedFiles() {
    return this.recentModifiedFiles.filter(file => {
      return !dayjs(file.updateTime).isYesterday() && !dayjs(file.updateTime).isToday()
    })
  }

  async fetchMyFavoriteFiles() {
    this.loading = true
    const res = await getLikeTemplateListApi({ withTemplate: true })
    this.favoriteTemplates = res.data?.list || []
    this.favoriteTemplateIds = res.data?.list?.map(item => item.id) || []
    this.loading = false
  }

  async fetchFavoriteTemplateIds() {
    this.loading = true
    const res = await getLikeTemplateListApi({ withTemplate: false })
    this.favoriteTemplateIds = res.data?.list || []
    this.loading = false
  }

  async likeTemplate(templateId: string) {
    await likeTemplateApi(templateId)
    this.favoriteTemplateIds.push(templateId)
  }
  

  async unlikeTemplate(templateId: string) {
    await removeLikeTemplateApi(templateId)
    this.favoriteTemplateIds = this.favoriteTemplateIds.filter(id => id !== templateId)
  }

}

export const EasyDrawContext = createContext<EasyDrawStore>(null)
